import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import { reportError } from "@app/services/error-reporter";
import type { ListMutate } from "@shared/hooks/useListMutate";
import {
    wsInplantsApi,
    wsInplantsServices,
    type ApiWsInplantContent,
    type WsInplantContent,
} from "../../../../api/wsInplants";
import { WS_INPLANT_CONTENT_CARD_LIST_INCLUDES } from "./hooks/useWsInplantContentsQuery";

export interface UseWsInplantContentMutationsParams {
    /** API локального обновления списка состава после успешных операций. */
    contentMutate: ListMutate<WsInplantContent>;
}

export interface UseWsInplantContentMutationsResult {
    createMutation: UseMutationResult<ApiWsInplantContent, Error, ApiWsInplantContent>;
    updateMutation: UseMutationResult<ApiWsInplantContent, Error, ApiWsInplantContent>;
    deleteMutation: UseMutationResult<void, Error, number>;
}

async function syncContentRow(
    contentMutate: ListMutate<WsInplantContent>,
    contentCode: number,
    action: "create" | "update",
): Promise<void> {
    const fresh = await wsInplantsServices.content.loadOne({
        flt: { Codes: [contentCode] },
        ...WS_INPLANT_CONTENT_CARD_LIST_INCLUDES,
    });
    if (fresh == null) {
        return;
    }

    if (action === "create") {
        contentMutate.createItems([fresh]);
        return;
    }

    contentMutate.updateItems([fresh]);
}

/**
 * Screen-level мутации элемента состава ВЗН УП.
 */
export function useWsInplantContentMutations({
    contentMutate,
}: UseWsInplantContentMutationsParams): UseWsInplantContentMutationsResult {
    const createMutation = useMutation({
        mutationKey: ["workshop", "wsInplant", "content", "create"],
        meta: { errorSource: "workshop.wsInplant.content.create" },
        mutationFn: (data: ApiWsInplantContent) => wsInplantsApi.content.insert(data),
        onSuccess: (result) => {
            notification.success("Элемент ВЗН добавлен");
            void syncContentRow(contentMutate, result.Code, "create").catch(reportError);
        },
    });

    const updateMutation = useMutation({
        mutationKey: ["workshop", "wsInplant", "content", "update"],
        meta: { errorSource: "workshop.wsInplant.content.update" },
        mutationFn: (data: ApiWsInplantContent) => wsInplantsApi.content.update(data),
        onSuccess: (result) => {
            notification.success("Элемент ВЗН сохранён");
            void syncContentRow(contentMutate, result.Code, "update").catch(reportError);
        },
    });

    const deleteMutation = useMutation({
        mutationKey: ["workshop", "wsInplant", "content", "delete"],
        meta: { errorSource: "workshop.wsInplant.content.delete" },
        mutationFn: (code: number) => wsInplantsApi.content.delete({ code }),
        onSuccess: (_result, code) => {
            notification.success("Элемент ВЗН удалён");
            contentMutate.deleteItem(code);
        },
    });

    return { createMutation, updateMutation, deleteMutation };
}
